<script setup lang="ts">
import { HomePageBestOffers } from '~/components/ui/home-page-best-offers'
import type {
  IBestOfferCard,
  IBestOfferCredit,
  IBestOfferDeposit,
  TBestOffer
} from '~/components/widget/best-offers/types'
import type { IResponseData } from '~/types'

const { t } = useI18n()

const tabs = [
  { name: 'common.deposits', value: 'deposits', endpoint: '/deposits/top-deposits' },
  { name: 'common.credits', value: 'credits', endpoint: '/credits/top-credit-rates' },
  { name: 'common.cards', value: 'cards', endpoint: '/card/featured-cards' }
]

const activeTab = ref('best-offer-tab-1')

const activeTabIndex = computed(() => {
  return parseInt(activeTab.value.replace('best-offer-tab-', '')) - 1
})

const url = computed(() => tabs[activeTabIndex.value].endpoint)

const {
  data: offers,
  status,
  error
} = useApi<IResponseData<TBestOffer[]>>(() => computed(() => url.value), {
  key: () => `best-offer-${url.value}`
})

const offersByType = computed(() => {
  const data = offers.value?.data || []
  const tabValue = tabs[activeTabIndex.value].value

  if (tabValue === 'deposits') {
    return {
      primary: (data as IBestOfferDeposit[]).map((item) => ({
        title: item.title,
        rate: t('widget.home_page.best_offers.deposit_rate', { rate: item.rate }),
        period: t('widget.home_page.best_offers.period_months', { period: item.period }),
        companyName: item.organization?.title || '',
        companyLogo: item.organization?.logo || '',
        url: item.url,
        isTop: true
      }))
    }
  }

  if (tabValue === 'credits') {
    return {
      primary: (data as IBestOfferCredit[]).map((item) => ({
        title: item.credit?.title || '',
        rate: t('widget.home_page.best_offers.credit_rate', { rate: item.rate }),
        period: t('widget.home_page.best_offers.period_months', { period: item.period }),
        companyName: item.credit?.organization?.title || '',
        companyLogo: item.credit?.organization?.logo || '',
        url: item.credit?.url || '',
        isTop: true
      }))
    }
  }

  if (tabValue === 'cards') {
    return {
      primary: (data as IBestOfferCard[]).map((item) => ({
        title: '',
        currency_type: item.currency_type,
        currencies: item.currencies,
        rate: item.title,
        period:
          item.issue_fee === 0
            ? t('widget.home_page.best_offers.free_issue')
            : t('widget.home_page.best_offers.issue_fee', { fee: item.issue_fee }),
        companyName: item.organization?.title || '',
        companyLogo: item.organization?.logo || '',
        url: item.url,
        isTop: true
      }))
    }
  }

  return { primary: [] }
})
</script>

<template>
  <HomePageBestOffers
    v-model:active-tab="activeTab"
    :tabs="tabs"
    :offers="offersByType"
    :is-loading="status === 'pending'"
    :error="error?.message"
  />
</template>
